import {ACCEPTED_IMAGE_MIME_TYPES, BlockerContext, showNotification} from 'platform/components';

import {useState} from 'react';

import {useUploadFileMutation} from '@dms/api/base';
import {useLazyGetSignatureImagePermanentUriQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {ApiError, Nullish} from 'shared/web';

import {handleApiError} from '../utils/handleApiError';
import {UploadWaitCancelled} from '../utils/UploadWaitCancelled';
import {useWaitForUploadedFile} from './useWaitForUploadedFile';

// An unfinished upload leaves the editor showing a local object URL only the author can resolve.
const LOCAL_PREVIEW_SOURCE = 'src="blob:';

/**
 * Wires image upload into an e-mail body editor: an inserted image is uploaded, and its local
 * preview source is swapped for a permanent URI once the storage service reports it processed.
 *
 * The pending count is owned here rather than mirrored from the editor, which has no unmount
 * cleanup and would leave the flag stuck on the two screens that render the field conditionally.
 *
 * The permanent-URI endpoint is named after the signature, where e-mail images shipped first; it
 * takes a file id, nothing signature-specific, behind the same permission.
 */
export function useEmailBodyImageUpload() {
  const [uploadFile] = useUploadFileMutation();
  const [getImagePermanentUri] = useLazyGetSignatureImagePermanentUriQuery();
  const {waitForUploadedFile, cancelPendingWaits} = useWaitForUploadedFile();

  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const isUploadingImages = pendingUploadCount > 0;

  const handleUploadImage = (image: File) => {
    // `input.accept` on the toolbar's file dialog is a hint the OS picker lets the author override.
    if (!ACCEPTED_IMAGE_MIME_TYPES.includes(image.type)) {
      showNotification.warning(i18n.t('entity.claimType.errors.fileTypeNotAllowed'));

      return Promise.reject(new Error(`Refused an image of type '${image.type}'.`));
    }

    setPendingUploadCount((count) => count + 1);

    return uploadFile({file: image})
      .unwrap()
      .then((upload) =>
        waitForUploadedFile(upload.fileId).then(() =>
          getImagePermanentUri({fileId: upload.fileId}).unwrap()
        )
      )
      .then((response) => response.permanentUri)
      .catch((error: ApiError | Error) => {
        reportUploadFailure(error, image.name);
        // Rethrown so the editor drops its local preview; otherwise the body keeps a refused `blob:`.
        throw error;
      })
      .finally(() => setPendingUploadCount((count) => Math.max(count - 1, 0)));
  };

  /**
   * Refuses a body whose images have not finished uploading, says why, and releases the form's submit
   * blocker so it does not settle as a successful send. Named `ensure` because it does all three.
   */
  const ensureBodyReadyToSend = (
    body: string | Nullish,
    blockerContext?: Pick<BlockerContext, 'reportApiFailure'>
  ) => {
    if (!isUploadingImages && !(body ?? '').includes(LOCAL_PREVIEW_SOURCE)) {
      return true;
    }

    showNotification.warning(i18n.t('richText.toolbar.uploadingImages'));
    blockerContext?.reportApiFailure?.();

    return false;
  };

  return {handleUploadImage, isUploadingImages, ensureBodyReadyToSend, cancelPendingWaits};
}

/**
 * `handleApiError` describes only RTK's error shapes, which are plain objects, so `instanceof Error`
 * separates a storage failure or timeout — which names the image — from an API error.
 */
function reportUploadFailure(error: ApiError | Error, imageName: string) {
  // Abandoned by the author, not failed.
  if (error instanceof UploadWaitCancelled) {
    return;
  }

  if (error instanceof Error) {
    showNotification.error(i18n.t('entity.photo.notifications.uploadFailed', {name: imageName}));

    return;
  }

  handleApiError(error);
}
